import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@/config/axios';
import { useAuth } from '@/lib/auth/AuthContext';
import { P, usePermissions } from '@/features/permissions';

export interface BiolockrDelivery {
  cancelled_at: string | null;
  created_at: string;
  created_by: string | null;
  delivered_at: string | null;
  error_code: string | null;
  /** biolockr knows this delivery: it can be refreshed and its records withdrawn, whatever the status. */
  has_remote_delivery: boolean;
  id: string;
  items: BiolockrDeliveryItem[];
  last_checked_at: string | null;
  patient_id: string;
  recipient_email: string;
  sent_at: string | null;
  status: BiolockrDeliveryStatus;
}

export interface BiolockrDeliveryCreate {
  items: { id: string; kind: BiolockrItemKind }[];
  patient_requested: true;
  recipient_email: string;
}

export interface BiolockrDeliveryItem {
  id: string;
  kind: BiolockrItemKind;
  opened_at: string | null;
  remote_kind: string;
  source_id: string;
  status: 'delivered' | 'pending' | 'withdrawn';
  title: string;
  version: number;
}

export type BiolockrDeliveryStatus =
  | 'awaiting_upload'
  | 'cancelled'
  | 'delivered'
  | 'failed'
  | 'not_matched'
  | 'queued'
  | 'sending'
  | 'sent'
  | 'withdrawn';

export type BiolockrItemKind = 'imaging' | 'report' | 'uploaded_document';

export interface BiolockrOptions {
  has_date_of_birth: boolean;
  items: BiolockrSendableItem[];
  recipient_email: string | null;
}

export interface BiolockrSendableItem {
  date: string | null;
  id: string;
  kind: BiolockrItemKind;
  remote_kind: string;
  title: string;
  version: number;
}

/**
 * Whether "Send to biolockr" should render at all.
 *
 * Two conditions, both required: the person holds `send_to_patient_app`, and
 * the server has the feature switched on. The flag is read from the server
 * (like the signing and imaging capabilities) so turning it off is a deploy,
 * not a frontend build. Anything short of an explicit `enabled: true` hides it.
 */
export function useBiolockrAvailable(): boolean {
  const { organization } = useAuth();
  const { can } = usePermissions();
  const orgId = organization?.organization_id;
  const allowed = can(P.SEND_TO_PATIENT_APP);
  const { data } = useQuery<{ enabled: boolean }>({
    retry: false,
    staleTime: Infinity,
    enabled: !!orgId && allowed,
    queryKey: ['biolockr-capabilities', orgId],
    queryFn: async () => (await clientApi.get(`${orgBase(orgId!)}/biolockr/capabilities`)).data,
  });
  return allowed && data?.enabled === true;
}

export function useBiolockrDeliveries(patientId: string, enabled: boolean) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  return useQuery<BiolockrDelivery[]>({
    enabled: enabled && !!orgId && !!patientId,
    queryKey: ['biolockr-deliveries', orgId, patientId],
    queryFn: async () => (await clientApi.get(base(orgId!, patientId))).data,
    // Keep the history moving while something is still on its way.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((d) => ['awaiting_upload', 'queued', 'sending'].includes(d.status))
        ? 5000
        : false,
  });
}

export function useBiolockrOptions(patientId: string, enabled: boolean) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  return useQuery<BiolockrOptions>({
    enabled: enabled && !!orgId && !!patientId,
    queryKey: ['biolockr-options', orgId, patientId],
    queryFn: async () => (await clientApi.get(`${base(orgId!, patientId)}/options`)).data,
  });
}

export function useCancelBiolockrDelivery(patientId: string) {
  return useDeliveryMutation<string>(
    patientId,
    async (url, deliveryId) => (await clientApi.post(`${url}/${deliveryId}/cancel`)).data,
  );
}

export function useRefreshBiolockrDelivery(patientId: string) {
  return useDeliveryMutation<string>(
    patientId,
    async (url, deliveryId) => (await clientApi.post(`${url}/${deliveryId}/refresh-status`)).data,
  );
}

export function useSendToBiolockr(patientId: string) {
  return useDeliveryMutation<BiolockrDeliveryCreate>(
    patientId,
    async (url, body) => (await clientApi.post(url, body)).data,
  );
}

export function useWithdrawBiolockrItem(patientId: string) {
  return useDeliveryMutation<{ deliveryId: string; itemId: string }>(
    patientId,
    async (url, { itemId, deliveryId }) =>
      (await clientApi.post(`${url}/${deliveryId}/items/${itemId}/withdraw`)).data,
  );
}

function base(orgId: string, patientId: string) {
  return `${orgBase(orgId)}/patients/${patientId}/biolockr-deliveries`;
}

function orgBase(orgId: string) {
  return `/api/v1/organizations/${orgId}`;
}

function useDeliveryMutation<T>(patientId: string, request: (url: string, input: T) => Promise<BiolockrDelivery>) {
  const { organization } = useAuth();
  const orgId = organization?.organization_id;
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: T) => request(base(orgId!, patientId), input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['biolockr-deliveries', orgId, patientId] }),
  });
}
